import { Selector } from "react-redux";
import { createSelector, ParametricSelector } from "reselect";

import { ILanguageExtended } from "@multiweb/types/contracts";
import { ExtendedOption, IAppState } from "@multiweb/types/react";
import { COMPARE_EQUAL, COMPARE_REFERENCE_FIRST, COMPARE_REFERENCE_SECOND, UNKNOWN_KEY_TEXT } from "./constants";
import { ExtendedLocalizableOption, GetTextSelectorParams, LocalizationItem } from "./types";
import { getText } from "./utils";
import CollatorOptions = Intl.CollatorOptions;

/**
 * Získá z globálního stavu podstrom patřící i18n modulu.
 */
const getModel = (state: IAppState) => state.core.i18n;

/**
 * Získá lokalizační klíče z aktuálního stavu.
 * @param state
 */
export const getLocalizationKeys: Selector<IAppState, string[]> = (state) => Object.keys(getModel(state).localizationItems);

/**
 * Získá lokalizační hodnotu pro zadaný klíč. Vrací undefined, pokud nebyla hodnota nalezena.
 * @param state
 * @param locKey Lokalizační klíč
 */
export const getLocalizationItem: Selector<IAppState, LocalizationItem | undefined, string> = (state, locKey) => getModel(state).localizationItems[locKey];

/**
 * Zjistí, zda je dostupná lokalizovaná hodnota pro lokalizační klíč.
 * @param state
 * @param locKey Lokalizační klíč
 */
export const hasLocalization: Selector<IAppState, boolean, string> = (state, locKey) => getLocalizationItem(state, locKey) !== undefined;

/**
 * Získá lokalizovaný hlavní text pro zadaný lokalizační klíč.
 * @param state
 * @param locKey Lokalizační klíč (např. "GUI-608276").
 * @param params Parametry lokalizace - pro nahrazení za placeholdery.
 * @return Pokud položka lokalizace nebyla načtena, vrací undefined. Pro načtenou položku lokalizace vrací lokalizovaný text.
 * (Včetně nahrazení placeholderů za `params`.)
 * V případě, že je lokalizovaný text `undefined`, vrací konstantu pro neznámou lokalizaci.
 */
export const getPrimaryText: ParametricSelector<IAppState, string, string | undefined> = (state, locKey, params?: string[]) => {
    const localizationItem = getLocalizationItem(state, locKey);
    return getText(localizationItem, "primaryText", params);
};

/**
 * Získá lokalizovaný alternativní text pro zadaný lokalizační klíč.
 * @param state
 * @param locKey Lokalizační klíč (např. "GUI-608276").
 * @param params Parametry lokalizace - pro nahrazení za placeholdery.
 * @return Pokud položka lokalizace nebyla načtena, vrací undefined. Pro načtenou položku lokalizace vrací lokalizovaný popis.
 * (Včetně nahrazení placeholderů za `params`.)
 * V případě, že je lokalizovaný popis `undefined`, vrací konstantu pro neznámou lokalizaci.
 */
export const getSecondaryText: ParametricSelector<IAppState, string, string | undefined> = (state, locKey, params?: string[]) => {
    const localizationItem = getLocalizationItem(state, locKey);
    return getText(localizationItem, "secondaryText", params);
};

/**
 * Vytvoří cacheovaný selektor, který dělá totéž, co {@link getPrimaryText}. Navíc je cacheovaný.
 * @param state
 * @param locKey Lokalizační klíč (např. "GUI-608276").
 * @param params Parametry lokalizace - pro nahrazení za placeholdery.
 */
export const createGetPrimaryText = () => createSelector<IAppState, GetTextSelectorParams, ReturnType<typeof getLocalizationItem>, string[] | undefined, string | undefined>(
    (state, { locKey }) => getLocalizationItem(state, locKey),
    (_state, { params }) => params,
    (localizationItem, params) => getText(localizationItem, "primaryText", params),
);

/**
 * Vytvoří cacheovaný selektor, který dělá totéž, co {@link getSecondaryText}. Navíc je cacheovaný.
 * @param state
 * @param locKey Lokalizační klíč (např. "GUI-608276").
 * @param params Parametry lokalizace - pro nahrazení za placeholdery.
 */
export const createGetSecondaryText = () => createSelector<IAppState, GetTextSelectorParams, ReturnType<typeof getLocalizationItem>, string[] | undefined, string | undefined>(
    (state, { locKey }) => getLocalizationItem(state, locKey),
    (_state, { params }) => params,
    (localizationItem, params) => getText(localizationItem, "secondaryText", params),
);

/**
 * Získá aktuálně nastavený kód jazyka aplikace..
 */
export const getLocaleCode: Selector<IAppState, string> = (state) => getModel(state).localeCode;

/**
 * Získá seznam aplikací podporovaných jazyků.
 */
export const getSupportedLanguages: Selector<IAppState, readonly ILanguageExtended[]> = (state) => getModel(state).supportedLanguages;

/**
 * Vytvoří selektor pro získání funkce pro porovnávání textu se zadanými parametry. Umožňuje porovnávat i prázdné hodnoty jako null a undefined.
 * Pořadí mezi prázdnými hodnotami (undefined, null, '') není definované. Funkce pro řazení využívá locale ze stavu. Vytvořená funkce je cachovaná
 * a nová se vytváří pouze při změně locale ve stavu.
 *
 * @param emptyFirst True pokud se mají prázdné položky řadit jako první, false pokud jako poslední.
 * @param options Nastavení pro funkci localeCompare. Viz. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
 */
export const createGetTextCompareFn = (emptyFirst = false, options?: CollatorOptions) => {
    const referenceTextEmpty = emptyFirst ? COMPARE_REFERENCE_FIRST : COMPARE_REFERENCE_SECOND;
    const comparedTextEmpty = emptyFirst ? COMPARE_REFERENCE_SECOND : COMPARE_REFERENCE_FIRST;
    return createSelector(
        getLocaleCode,
        (locale) => (referenceText?: string | null, comparedText?: string | null) => {
            if (referenceText === comparedText) {
                return COMPARE_EQUAL;
            } else if (!referenceText) {
                return referenceTextEmpty;
            } else if (!comparedText) {
                return comparedTextEmpty;
            } else {
                return referenceText.localeCompare(comparedText, locale, options);
            }
        },
    );
};

/**
 * Vytvoří cachovaný selector pro získávání přeložených staticky definovaných rozšířených options.
 * Lze díky tomu definovat i metadata, která se spolu s lokalizovaným textem mají uchovávat.
 * @param options ExtendedOptions k přeložení.
 */
export const createGetLocalizedOptions = <V, T = undefined>(options: ReadonlyArray<ExtendedLocalizableOption<V, T>>) => createSelector<IAppState, string | undefined, ReadonlyArray<ExtendedOption<V, T>>>(
    options.map(({ locKey }) => (state: IAppState) => getPrimaryText(state, locKey)),
    (...localizedTexts) => localizedTexts.map((text, index) =>
        ({
            text: text ?? UNKNOWN_KEY_TEXT,
            value: options[index].value,
            meta: options[index].meta,
        })),
);
